import { memo, useCallback, useMemo } from 'react';
import { styled, Typography } from '@mui/material';
import type { CellContext } from '@tanstack/react-table';

import { isUndefined } from 'utils/typeChecks';
import { formatHeliosDateValue } from 'utils/dateAndTime';
import { Checkbox } from 'components/mui/common/Checkbox';
import { type HeliosRecordDataTableColumnSettings, RoundedToValue } from 'types/dataHelios';
import type { HeliosCellData } from 'types/helios/agendaListing';
import { roundedToNumber } from 'utils/helios/rounding';
import { useLocaleCodeIsoNorma } from 'utils/hooks';
import type { FormItemUiDefinition } from '../../../../../types/formItem.ts';
import { getUiDefinitionContainerStyles, getUiDefinitionTextStyles, mapTextAlignToCss } from '../../../../../utils/styles.ts';
import { formatHeliosNumber } from 'utils/helios/numberFormatting';

const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isClickable' && prop !== 'hasValue',
})<{ isClickable?: boolean; hasValue?: boolean }>(({ isClickable, hasValue, theme }) => ({
  transition: isClickable ? 'all' : 'none',
  ...(isClickable
    ? {
        '&:hover': {
          textDecoration: 'underline',
          cursor: 'pointer',
        },
      }
    : {
        cursor: 'default',
      }),
  color: !hasValue ? `${theme.palette.text.secondary}` : 'inherit',
  fontStyle: !hasValue ? 'italic' : 'normal',
}));

interface Props<T, P> {
  value?: string;
  row: CellContext<T, P>;
  onClick?: (row: T, cell: keyof P) => unknown;
  isClickable?: boolean;
  dataType?: string;
  data?: HeliosCellData;
  settings?: HeliosRecordDataTableColumnSettings;
  formItemUiDefinition?: FormItemUiDefinition;
}

const TextBase = <T, P>({ value, row, onClick, isClickable, dataType, data, formItemUiDefinition }: Props<T, P>) => {
  const locale = useLocaleCodeIsoNorma();

  const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
  const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);

  const handleRowClick = useCallback(() => {
    if (onClick) {
      onClick(row.row.original, row.column.id as keyof P);
    }
  }, [onClick, row.column.id, row.row.original]);

  const formattedValue = useMemo(() => {
    if (!value) return '';

    switch (dataType) {
      case 'DateTime': {
        const udpType = data?.userDefinedAttributeType;
        const columnVariant = data?.formatting?.formatValue?.typeSpecific?.formatDateTime?.variant;
        const formatVariant: 'Date' | 'DateTime' | 'Time' | 'MonthYear' | null = columnVariant ?? udpType ?? null;
        return formatHeliosDateValue(value, formatVariant, 'dd.MM.yyyy HH:mm') ?? value;
      }
      case 'Boolean':
        return <Checkbox checked={value === 'true'} disabled />;
      case 'Decimal': {
        const formatting = data?.formatting;
        if (formatting) {
          const rounding = roundedToNumber[formatting?.formatValue?.typeSpecific?.formatNumber?.roundTo ?? RoundedToValue.Hundreds];
          const rawValue = data?.rawValue;
          const parsedNumericValue = typeof rawValue === 'number' ? rawValue : typeof rawValue === 'string' ? Number(rawValue) : NaN;

          if (!Number.isFinite(parsedNumericValue)) {
            return value;
          }

          const numericValue = formatHeliosNumber(parsedNumericValue, locale, {
            minimumFractionDigits: Math.max(0, rounding),
            maximumFractionDigits: Math.max(0, rounding),
            thousandsSeparator: formatting?.formatValue?.typeSpecific?.formatNumber?.thousandsSeparator,
            roundTo: formatting?.formatValue?.typeSpecific?.formatNumber?.roundTo,
            mathRound: formatting?.formatValue?.typeSpecific?.formatNumber?.mathRound,
          });

          return `${numericValue} ${formatting?.formatValue?.typeSpecific?.formatNumber?.unit || ''}`;
        }
        return value;
      }
      default:
        return value;
    }
  }, [data?.formatting, data?.rawValue, data?.userDefinedAttributeType, dataType, locale, value]);

  const hasValue = !isUndefined(value) && String(value).length > 0 && value !== '{}';
  const textAlign = mapTextAlignToCss(data?.formatting?.formatValue?.textAlign);

  return (
    <StyledTypography
      variant="body1"
      isClickable={isClickable}
      hasValue={hasValue}
      noWrap
      onClick={handleRowClick}
      style={{ ...boxStyles, ...textStyles, textAlign, lineHeight: '24px' }}>
      {hasValue ? formattedValue : ''}
    </StyledTypography>
  );
};

export const ValueHelios = memo(TextBase) as typeof TextBase;
